import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, User, CreditCard, Smartphone, MessageSquare, ChevronDown, ChevronUp, Wallet, Ban, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/crypto-data";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";

interface Profile {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  numeric_id: string | null;
  is_banned: boolean;
  created_at: string;
}

interface WalletRow {
  crypto_symbol: string;
  balance: number;
}

interface Transaction {
  id: string;
  crypto_symbol: string;
  crypto_name: string;
  amount: number;
  total_usd: number;
  status: string;
  created_at: string;
}

interface Device {
  id: string;
  ip_address: string | null;
  user_agent: string | null;
  is_blocked: boolean;
  last_login: string;
}

interface UserData extends Profile {
  wallets: WalletRow[];
  totalBalanceUsd: number;
  transactions: Transaction[];
  devices: Device[];
  transactionCount: number;
}

const STABLE_USD: Record<string, number> = {
  USDT: 1, USDC: 1, DAI: 1, USD: 1,
};

const AdminUsers = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [prices, setPrices] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [{ data: profiles }, { data: wallets }, { data: transactions }, { data: devices }] = await Promise.all([
        supabase.from("profiles").select("*"),
        supabase.from("wallets").select("user_id, crypto_symbol, balance"),
        supabase.from("transactions").select("id, user_id, crypto_symbol, crypto_name, amount, total_usd, status, created_at"),
        supabase.from("user_devices").select("id, user_id, ip_address, user_agent, is_blocked, last_login"),
      ]);

      // Fetch prices for known symbols
      const symbols = Array.from(new Set((wallets || []).map((w: any) => w.crypto_symbol)));
      const priceMap: Record<string, number> = {};
      symbols.forEach((s) => { if (STABLE_USD[s]) priceMap[s] = 1; });
      try {
        const { data: priceData } = await supabase.functions.invoke("crypto-prices", { body: { symbols } });
        if (priceData?.prices) Object.assign(priceMap, priceData.prices);
      } catch { /* ignore */ }
      setPrices(priceMap);

      const usersData: UserData[] = (profiles || []).map((profile: any) => {
        const userWallets: WalletRow[] = (wallets || [])
          .filter((w: any) => w.user_id === profile.user_id)
          .map((w: any) => ({ crypto_symbol: w.crypto_symbol, balance: Number(w.balance) }));

        const totalBalanceUsd = userWallets.reduce((sum, w) => {
          const p = priceMap[w.crypto_symbol] ?? 0;
          return sum + w.balance * p;
        }, 0);

        const userTx = (transactions || []).filter((t: any) => t.user_id === profile.user_id);
        const userDev = (devices || []).filter((d: any) => d.user_id === profile.user_id);

        return {
          ...profile,
          wallets: userWallets,
          totalBalanceUsd,
          transactions: userTx,
          devices: userDev,
          transactionCount: userTx.length,
        };
      });

      // Sort by total balance descending
      usersData.sort((a, b) => b.totalBalanceUsd - a.totalBalanceUsd);
      setUsers(usersData);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleBan = async (userId: string, banned: boolean) => {
    const reason = banned ? prompt("سبب الحظر (اختياري):") || "" : "";
    if (banned && !confirm("تأكيد حظر هذا المستخدم؟ سيتم منعه من الدخول.")) return;
    if (!banned && !confirm("رفع الحظر عن هذا المستخدم؟")) return;
    try {
      const { error } = await supabase.rpc("admin_set_user_ban" as any, {
        _target_user_id: userId,
        _banned: banned,
        _reason: reason || null,
      });
      if (error) throw error;
      toast({ title: banned ? "تم الحظر" : "تم رفع الحظر" });
      fetchUsers();
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("ar-SA", { year: "numeric", month: "short", day: "numeric" });

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="glass border-border">
      <CardHeader>
        <CardTitle className="text-foreground flex items-center gap-2 flex-wrap">
          <User className="w-5 h-5" />
          المستخدمون ({users.length}) — مرتبة حسب الرصيد (الأعلى أولاً)
          <span className="ms-auto text-sm font-normal text-muted-foreground">
            الرصيد الإجمالي للجميع:{" "}
            <span className="text-primary font-bold text-base">
              {formatCurrency(users.reduce((sum, u) => sum + (u.totalBalanceUsd || 0), 0))}
            </span>
          </span>
        </CardTitle>
      </CardHeader>

      <CardContent>
        {users.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">لا يوجد مستخدمون</div>
        ) : (
          <div className="space-y-3">
            {users.map((user) => (
              <Collapsible
                key={user.id}
                open={expandedUser === user.id}
                onOpenChange={() => setExpandedUser(expandedUser === user.id ? null : user.id)}
              >
                <div className={`border rounded-lg overflow-hidden ${user.is_banned ? "border-destructive/60 bg-destructive/5" : "border-border"}`}>
                  <CollapsibleTrigger asChild>
                    <div className="flex flex-wrap items-center justify-between gap-3 p-4 cursor-pointer hover:bg-secondary/50 transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate flex items-center gap-2">
                            {user.full_name || "بدون اسم"}
                            {user.is_banned && <Badge variant="destructive" className="text-[10px]">محظور</Badge>}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          <p className="text-xs text-primary font-mono" dir="ltr">ID: {user.numeric_id || "-"}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">الرصيد الإجمالي</p>
                          <p className="font-bold text-primary">{formatCurrency(user.totalBalanceUsd)}</p>
                        </div>
                        <div className="text-center hidden sm:block">
                          <p className="text-xs text-muted-foreground">مشتريات</p>
                          <p className="font-bold text-foreground">{user.transactionCount}</p>
                        </div>
                        <div className="text-center hidden sm:block">
                          <p className="text-xs text-muted-foreground">مسجّل منذ</p>
                          <p className="text-xs text-foreground">{formatDate(user.created_at)}</p>
                        </div>
                        <Button
                          variant={user.is_banned ? "outline" : "ghost"}
                          size="sm"
                          className={user.is_banned ? "text-crypto-green border-crypto-green" : "text-destructive"}
                          onClick={(e) => { e.stopPropagation(); toggleBan(user.user_id, !user.is_banned); }}
                        >
                          {user.is_banned ? <><ShieldCheck className="w-4 h-4 ml-1" />رفع الحظر</> : <><Ban className="w-4 h-4 ml-1" />حظر</>}
                        </Button>
                        <Button variant="ghost" size="sm">
                          {expandedUser === user.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="border-t border-border p-4 bg-secondary/30 space-y-4">
                      <div>
                        <h4 className="font-medium text-foreground flex items-center gap-2 mb-2">
                          <Wallet className="w-4 h-4" /> المحافظ ({user.wallets.length})
                        </h4>
                        {user.wallets.length > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {user.wallets
                              .slice()
                              .sort((a, b) => (b.balance * (prices[b.crypto_symbol] || 0)) - (a.balance * (prices[a.crypto_symbol] || 0)))
                              .map((w) => (
                                <div key={w.crypto_symbol} className="p-2 rounded bg-background/40 border border-border/40">
                                  <p className="text-xs text-muted-foreground">{w.crypto_symbol}</p>
                                  <p className="font-mono text-sm text-foreground">{w.balance.toFixed(6)}</p>
                                  {prices[w.crypto_symbol] && (
                                    <p className="text-xs text-primary">{formatCurrency(w.balance * prices[w.crypto_symbol])}</p>
                                  )}
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-sm">لا توجد محافظ</p>
                        )}
                      </div>

                      <div>
                        <h4 className="font-medium text-foreground flex items-center gap-2 mb-2">
                          <CreditCard className="w-4 h-4" /> مشتريات العملات ({user.transactions.length})
                        </h4>
                        {user.transactions.length > 0 ? (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="border-border">
                                  <TableHead className="text-right">التاريخ</TableHead>
                                  <TableHead className="text-right">العملة</TableHead>
                                  <TableHead className="text-right">المبلغ</TableHead>
                                  <TableHead className="text-right">الحالة</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {user.transactions.slice(0, 10).map((tx) => (
                                  <TableRow key={tx.id} className="border-border">
                                    <TableCell className="text-sm">{formatDate(tx.created_at)}</TableCell>
                                    <TableCell>{tx.crypto_symbol}</TableCell>
                                    <TableCell className="text-primary font-bold">{formatCurrency(tx.total_usd)}</TableCell>
                                    <TableCell>
                                      <Badge variant="outline">{tx.status}</Badge>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-sm">لا توجد مشتريات</p>
                        )}
                      </div>

                      <div>
                        <h4 className="font-medium text-foreground flex items-center gap-2 mb-2">
                          <Smartphone className="w-4 h-4" /> الأجهزة ({user.devices.length})
                        </h4>
                        {user.devices.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {user.devices.map((d) => (
                              <Badge key={d.id} variant={d.is_blocked ? "destructive" : "outline"} className="text-xs">
                                {d.ip_address || "?"} · {d.is_blocked ? "محظور" : "نشط"}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-sm">لا توجد أجهزة</p>
                        )}
                      </div>

                      {user.is_banned && (user as any).banned_reason && (
                        <div className="p-3 rounded bg-destructive/10 border border-destructive/40">
                          <p className="text-xs text-destructive">سبب الحظر: {(user as any).banned_reason}</p>
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminUsers;
